<template>
  <div>
    <AppLoadingPage v-if="$apollo.loading" />
    <div v-else>
      <div
        v-for="([disciplineName, values], index) in qualificationsGrouped"
        :key="index"
        class="my-4"
      >
        <AppTableHeader>
          <template #left>
            {{ $t(`common:discipline.${disciplineName}`) }}
          </template>
          <template #right>
            <v-btn
              v-if="manageOverrides"
              class="btn-with-icon mx-2"
              variant="text"
              data-test="newOverrideQualification"
              @click="newOverride(disciplineName)"
            >
              <div class="bg-edit btn-icon-with-text">
                <v-icon size="small" class="text-white"> $iconPlus </v-icon>
              </div>
              <span class="text-white">{{
                $t("common:button.add", "Add")
              }}</span>
            </v-btn>
          </template>
        </AppTableHeader>

        <div class="px-2 mt-5">
          <template v-if="values.length == 0">
            {{
              $t("people:PersonProfile.qualifications.noQualifications.text")
            }}
          </template>
          <template v-for="(g, groupIndex) in values" v-else>
            <div
              v-if="$vuetify.display.mdAndUp"
              :key="`group-${groupIndex}-desktop`"
            >
              <div class="w-100" style="overflow-x: auto">
                <table
                  class="entsys--table additional-borders"
                  style="width: auto"
                >
                  <thead>
                    <tr>
                      <th class="text-left">
                        {{ g.qualificationGroupName }}
                      </th>
                      <th
                        v-for="(h, headerIndex) in g.statuses"
                        :key="`h-${headerIndex}`"
                        class="text-center"
                      >
                        {{ h.header }}
                      </th>
                      <th v-if="manageOverrides" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="no-hover">
                      <td>
                        <template v-if="g.person && g.horse">
                          <AppShowPersonName :person="g.person" /> /
                          <AppShowHorseName :horse="g.horse" />
                        </template>
                        <template v-else>
                          <AppShowPersonName
                            v-if="g.person"
                            :person="g.person"
                          />
                          <AppShowHorseName
                            v-else-if="g.horse"
                            :horse="g.horse"
                          />
                        </template>
                      </td>
                      <td
                        v-for="(s, statusIndex) in g.statuses"
                        :key="`s-${statusIndex}`"
                        class="text-center"
                      >
                        <AppShowQualificationStatus
                          :status="s.status"
                          class="ma-auto"
                          no-margin
                          @click="
                            expandItem(
                              `${disciplineName}-${groupIndex}-${s.header}-${statusIndex}`,
                              disciplineName,
                              g.qualificationGroupName
                            )
                          "
                        />
                      </td>
                      <td v-if="manageOverrides">
                        <v-btn-icon
                          class="bg-edit-green"
                          :aria-label="$t('common:editButton.alt')"
                          data-test="editOverrideQualification"
                          size="small"
                          icon
                          @click="editOverride(g)"
                        >
                          <v-icon color="white" size="small"> $edit </v-icon>
                        </v-btn-icon>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <AppTabItems
                :key="activeTabKey"
                v-model="activeTab[disciplineName][g.qualificationGroupName]"
                disable-min-height
              >
                <AppTabItem
                  v-for="(expandedItem, expandedIndex) in g.statuses"
                  :id="`${disciplineName}-${groupIndex}-${expandedItem.header}-${expandedIndex}`"
                  :key="`e-${expandedIndex}`"
                  :value="`${disciplineName}-${groupIndex}-${expandedItem.header}-${expandedIndex}`"
                  :transition="false"
                >
                  <v-card color="strong" class="pa-4" tile>
                    <QualificationExpanded
                      :qualification="expandedItem.qualification"
                      :qualification-rules-statuses="
                        expandedItem.qualificationRulesStatuses
                      "
                      :person-id="g.person ? g.person.id : null"
                      :horse-id="g.horse ? g.horse.id : null"
                      @close="
                        closeTab(
                          `${disciplineName}-${groupIndex}-${expandedItem.header}-${expandedIndex}`,
                          disciplineName,
                          g.qualificationGroupName
                        )
                      "
                    />
                  </v-card>
                </AppTabItem>
              </AppTabItems>

              <div class="my-3">
                {{
                  $t(
                    "calendar:PersonProfile.qualifications.qualificationGroupTable.hint"
                  )
                }}
              </div>
            </div>
            <div v-else :key="`group-${groupIndex}`">
              <v-divider v-if="groupIndex != 0" class="my-5" />
              <div class="mb-2 px-3">
                <div>
                  <strong class="text-left">{{
                    g.qualificationGroupName
                  }}</strong>
                </div>

                <div>
                  <template v-if="g.person && g.horse">
                    <AppShowPersonName :person="g.person" class="d-inline" />
                    /
                    <AppShowHorseName :horse="g.horse" class="d-inline" />
                  </template>
                  <template v-else>
                    <AppShowPersonName v-if="g.person" :person="g.person" />
                    <AppShowHorseName v-else-if="g.horse" :horse="g.horse" />
                  </template>
                </div>
              </div>
              <v-expansion-panels
                v-model="activeTab[disciplineName][g.qualificationGroupName]"
                variant="accordion"
              >
                <v-expansion-panel
                  v-for="(h, headerIndex) in g.statuses"
                  :key="`h-${headerIndex}`"
                >
                  <v-expansion-panel-title>
                    <div class="d-flex align-center">
                      <strong>{{ h.header }}</strong>
                      <div class="ml-auto mr-5">
                        <AppShowQualificationStatus :status="h.status" />
                      </div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text color="strong">
                    <QualificationExpanded
                      :qualification="h.qualification"
                      :qualification-rules-statuses="
                        h.qualificationRulesStatuses
                      "
                      :person-id="g.person ? g.person.id : null"
                      :horse-id="g.horse ? g.horse.id : null"
                    />
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </template>
        </div>
      </div>
    </div>
    <QualificationOverrideForm
      v-if="manageOverrides"
      ref="overrideForm"
      v-model="overrideDialog"
      v-bind="{ horseId, personId, qualificationStatuses }"
    />
  </div>
</template>
<script>
import gql from "fraql";
import { useDisciplineHelper } from "@composables/masterdata/competitions/disciplines";
import { groupBy, sortBy, uniq } from "lodash-es";
import AppTableHeader from "@/components/elements/table/AppTableHeader.vue";
import AppLoadingPage from "@elements/AppLoadingPage.vue";
import AppShowPersonName from "@AppShows/person/AppShowPersonName.vue";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";
import QualificationOverrideForm from "./QualificationOverrideForm.vue";
import QualificationExpanded from "./QualificationExpanded.vue";
import AppShowQualificationStatus from "@AppShows/AppShowQualificationStatus.vue";

import AppTabItems from "@elements/tab/AppTabItems.vue";
import AppTabItem from "@elements/tab/AppTabItem.vue";

export default {
  name: "ProfileQualificationsTab",
  components: {
    AppTableHeader,
    AppShowPersonName,
    AppShowHorseName,
    QualificationExpanded,
    AppLoadingPage,
    QualificationOverrideForm,
    AppShowQualificationStatus,

    AppTabItems,
    AppTabItem
  },
  props: {
    personId: {
      type: String,
      default: null
    },
    horseId: {
      type: String,
      default: null
    }
  },
  setup() {
    return {
      ...useDisciplineHelper()
    };
  },
  data() {
    return {
      activeTab: {},
      activeTabKey: false,
      mountedTabs: [],
      qualificationStatuses: [],
      overrideDialog: false
    };
  },
  computed: {
    manageOverrides() {
      return this.$auth.checkUserCanPerformMutationsInScopeAny(
        "qualificationGroupUpdateOverrides"
      );
    },
    qualificationsGrouped() {
      let grouped = {};
      this.masterDisciplines.forEach(discipline => {
        grouped[discipline.name] = [];
      });

      // Group by disciplines first
      grouped = Object.assign(
        grouped,
        groupBy(this.qualificationStatuses, "qualification.discipline.name")
      );
      // Get all uniqe groups
      const qualificationGroupNames = uniq(
        sortBy(
          this.qualificationStatuses,
          "qualification.qualificationGroup.rank"
        ).map(s => {
          return s.qualification.qualificationGroup.name;
        })
      );

      // Loop disciplines and then group by qualificationGroupName as a new object
      Object.keys(grouped).forEach(disciplineName => {
        const newGrouped = [];
        qualificationGroupNames.forEach(groupName => {
          // Filter by qualification group name
          const filteredByQualificationGroup = grouped[disciplineName].filter(
            s => s.qualification.qualificationGroup.name == groupName
          );

          if (filteredByQualificationGroup.length) {
            const groupedByHorsesAndPerson = groupBy(
              filteredByQualificationGroup,
              this.personId ? "horse.name" : "person.name"
            );
            Object.entries(groupedByHorsesAndPerson).forEach(
              ([, qualifications]) => {
                newGrouped.push({
                  disciplineId: this.masterDisciplines.find(
                    x => x.name == disciplineName
                  )?.id,
                  qualificationGroupName: groupName,
                  person: qualifications[0].person,
                  horse: qualifications[0].horse,
                  statuses: sortBy(
                    qualifications.map(s => ({
                      rank: s.qualification.rank,
                      qualification: s.qualification,
                      qualificationRulesStatuses: s.qualificationRulesStatuses,
                      status: s.status,
                      header: s.qualification.name
                    })),
                    "rank"
                  )
                });
              }
            );
          }
        });
        grouped[disciplineName] = newGrouped;
      });
      if (Object.keys(this.activeTab).length == 0) {
        // Create object where to store what expanded item to display in each discipline's qualificationGroup
        Object.entries(grouped).forEach(([disciplineName, values]) => {
          this.activeTab[disciplineName] = {};
          values.forEach(x => {
            this.activeTab[disciplineName][x.qualificationGroupName] = false;
          });
        });
      }
      return Object.entries(grouped).filter(g => g[1].length);
    }
  },
  mounted() {
    // Coming back, clicking button, shows and quickly dissapears
    // This prevents it
    this.activeTab = {};
  },
  apollo: {
    qualificationStatuses: {
      query: gql`
        query qualificationStatuses($personId: ID, $horseId: ID, $onlyStartedOrOverridden: Boolean) {
          qualificationStatuses(personId: $personId, horseId: $horseId, onlyStartedOrOverridden: $onlyStartedOrOverridden) {
            status
            qualification {
              id
              name
              rank
              explanationText
              disciplineId
              discipline @client {
                id
                name
              }
              qualificationGroupId
              qualificationGroup @client {
                id
                name
                rankedEntity
              }
              qualificationRules {
                id
                name
                explanationText
              }
            }
            qualificationRulesStatuses
            person {
              id
              ${AppShowPersonName.fragments.person}
            }
            horse {
              id
              name
            }
          }
        }
      `,
      variables() {
        return {
          personId: this.personId,
          horseId: this.horseId,
          onlyStartedOrOverridden: true
        };
      },
      update({ qualificationStatuses }) {
        return JSON.parse(JSON.stringify(qualificationStatuses));
      },
      fetchPolicy: "cache-and-network"
    }
  },
  methods: {
    expandItem(tabId, discipline, group) {
      if (!this.mountedTabs.includes(tabId)) {
        this.mountedTabs.push(tabId);
      }

      if (
        this.activeTab[discipline][group] &&
        this.activeTab[discipline][group] != tabId
      ) {
        this.activeTab[discipline][group] = tabId;
      } else {
        this.activeTab[discipline][group] = this.activeTab[discipline][group]
          ? null
          : tabId;
      }

      //this.activeTabKey = !this.activeTabKey;
    },
    closeTab(tabId, discipline, group) {
      this.activeTab[discipline][group] = null;
      //this.activeTabKey = !this.activeTabKey;
    },
    newOverride(disciplineName) {
      const discipline = this.masterDisciplines.find(
        x => x.name == disciplineName
      );
      this.$refs.overrideForm.setForm(
        {
          disciplineId: discipline.id,
          horseId: this.horseId,
          personId: this.personId
        },
        false
      );
      this.overrideDialog = true;
    },
    editOverride(item) {
      this.$refs.overrideForm.setForm(
        {
          disciplineId: item.disciplineId,
          qualificationGroupId:
            item.statuses[0].qualification.qualificationGroupId,
          personId: item.person?.id,
          horseId: item.horse?.id
        },
        true
      );
      this.overrideDialog = true;
    }
  }
};
</script>
<style scoped lang="scss">
table.additional-borders {
  td {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }
}
</style>
